<template>
  <div
    class="focus-view row reverse justify-end"
    :class="correctedViewClasses"
  >
    <div
      v-if="(desktopMode || showDefaultFocusOnMobile) && (hasDefaultFocusContentSlot || (desktopMode && isFocusing))"
      class="col-12"
      :class="{[correctFocusBreakpoint]: true}"
    >
      <component
        :is="focusTag"
        ref="focusComponent"
        :class="correctedFocusClasses"
        :style="correctedFocusStyles"
      >
        <template v-if="desktopMode && isFocusing">
          <q-toolbar :class="focusToolbarClass">
            <q-btn
              round
              dense
              flat
              icon="mdi-close"
              @click="unfocus"
            />
            <q-toolbar-title>
              {{ currentFocus.title }}
            </q-toolbar-title>
          </q-toolbar>

          <portal-target :name="`${id}-focus-target`" />
        </template>
        <slot v-else name="default-focus" />
      </component>
    </div>

    <div class="col-12" :class="{[correctMainBreakpoint]: desktopMode && (hasDefaultFocusContentSlot || isFocusing)}">
      <slot name="main" />
    </div>

    <content-dialog
      v-if="!desktopMode"
      :model-value="isFocusing"
      :title="currentFocus ? currentFocus.title : undefined"
      :width="currentFocus ? currentFocus.dialogWidth : undefined"
      :card-class="dialogClass"
      fullscreen-mobile
      close-icon="mdi-arrow-left"
      @update:model-value="unfocus"
    >
      <portal-target :name="`${id}-focus-target`" />
    </content-dialog>
  </div>
</template>

<script>
import { strToObj } from 'src/helpers/globals'

/**
 * FocusView is the outer wrapper of the entire focus scaffolding
 * The view will contain FocusContainers which contains FocusFields
 *
 * The idea is that a FocusContainer can be focussed on for displaying details
 *  in the sidebar (or dialog on mobile)
 *
 * SLOTS:
 * - main: main section
 * - default-focus: default content to display in the FocusView sidebar (sidebar is hidden if this slot is not specified)
 */
export default {
  name: 'FocusView',
  props: {
    // A unique ID to identify this View
    id: {
      type: String,
      required: true
    },
    // Force FocusView into mobile mode (only uses dialogs instead of sidebar)
    onlyMobileMode: {
      type: Boolean,
      default: false
    },
    // Classes to apply to the outer view
    viewClass: {
      type: [String, Object]
    },
    // Breakpoint at which desktop mode will be used on the FocusView
    desktopBreakpoint: {
      type: Number,
      default: 1024
    },
    // Optionally show the default-focus contents above the main content on mobile (false = it will be hidden on mobile)
    showDefaultFocusOnMobile: {
      type: Boolean,
      default: false
    },
    // Tag to use on the focussed content
    focusTag: {
      type: [String, Object, Function],
      default: 'div'
    },
    // Classes to apply to the focussed content (also applies to dialog on mobile mode)
    focusClass: {
      type: [String, Object]
    },
    // Classes to apply to the toolbar on focussed content (only applies to desktop)
    focusToolbarClass: {
      type: [String, Object]
    },
    // Classes to apply to the dialog on mobile mode
    dialogClass: {
      type: [String, Object]
    },
    // Breakpoints to apply to separate columns on desktop mode
    mainBreakpoint: {
      type: String,
      default: 'col-md-7 col-lg-8'
    },
    focusBreakpoint: {
      type: String,
      default: 'col-md-5 col-lg-4'
    }
  },
  data () {
    return {
      currentFocus: null
    }
  },
  computed: {
    isFocusing () {
      return !!this.currentFocus
    },
    hasDefaultFocusContentSlot () {
      return !!this.$slots['default-focus']
    },
    desktopMode () {
      return !this.onlyMobileMode && this.windowWidth >= this.desktopBreakpoint
    },
    correctMainBreakpoint () {
      return this.currentFocus && this.currentFocus.mainBreakpoint ? this.currentFocus.mainBreakpoint : this.mainBreakpoint
    },
    correctFocusBreakpoint () {
      return this.currentFocus && this.currentFocus.focusBreakpoint ? this.currentFocus.focusBreakpoint : this.focusBreakpoint
    },
    correctedFocusClasses () {
      return strToObj(this.focusClass, {
        'default-focus': !this.isFocusing
      })
    },
    correctedViewClasses () {
      return strToObj(this.viewClass, {
        'can-animate': this.hasDefaultFocusContentSlot
      })
    },
    correctedFocusStyles () {
      const obj = {}
      if (this.currentFocus && this.currentFocus.topPosition !== null) {
        obj['margin-top'] = this.currentFocus.topPosition
      }
      return obj
    }
  },
  methods: {
    unfocus () {
      this.currentFocus = null
    },
    busOnFocus (params) {
      let isAlreadyFocussingOnElement = false

      if (this.currentFocus) {
        // First remove focus of another element - can cause Vue to not reload the portal properly
        if (this.currentFocus.linkKey !== params.linkKey) {
          this.unfocus()
        }
        else {
          isAlreadyFocussingOnElement = true
        }
      }
      this.$nextTick(() => {
        this.currentFocus = params

        // Optionally emit an event when a container is focused on
        if (!isAlreadyFocussingOnElement || params.allowRefocus) {
          if (params.onFocus) {
            this.timed(() => {
              params.onFocus()
            })
          }
          if (this.desktopMode && params.onDesktopFocus) {
            this.timed(() => {
              const target = this.$refs.focusComponent.$el
              params.onDesktopFocus(target)
            })
          }
        }
      })
    },
    busOnUnfocus (linkKey) {
      if (this.currentFocus && this.currentFocus.linkKey === linkKey) {
        this.unfocus()
      }
    }
  },
  mounted () {
    this.$bus.on(`focus:${this.id}`, this.busOnFocus)
    this.$bus.on(`unfocus:${this.id}`, this.busOnUnfocus)
  },
  beforeUnmount () {
    this.$bus.off(`focus:${this.id}`, this.busOnFocus)
    this.$bus.off(`unfocus:${this.id}`, this.busOnUnfocus)
  }
}
</script>

<style lang="scss" src="./focusView.scss" />
